/**
 * Two product-page repairs, both driven by markup the theme renders but does not
 * wire up correctly on this store.
 *
 * 1. Sticky add-to-cart bar
 *    The PDP ends up with two nodes carrying the same id,
 *    `ProductSubmitButton-<section>`: the theme's own submit button (hidden with
 *    display:none) and the copy the quantity-selector block re-renders. StickyAtc
 *    resolves its reference with getElementById, which returns the *first* match —
 *    the hidden one. Measuring it yields a 0x0 rect, so the bar never concludes the
 *    shopper scrolled past the add-to-cart and stays translated off-screen forever,
 *    on desktop and mobile alike. StickyAtc keeps that reference in a plain
 *    `mainAtcBtn` property and re-reads it on every scroll, so pointing it at the
 *    visible button is enough — no need to touch the theme's minified code.
 *
 * 2. "Personalized" bar
 *    Personalisation fields come from Bold Product Options, which injects them into
 *    the form after load, so no server-side check can know whether a product has
 *    any. The bar therefore ships hidden and is revealed only once a real
 *    personalisation input shows up. Hidden-only properties (the premium-quality
 *    upsell, Bold's own bookkeeping fields) do not count — a shirt with nothing to
 *    personalise must not advertise that it is personalised.
 */
(function () {
  /* ---------- 1. sticky add-to-cart ---------- */

  function visibleSubmitButton(section) {
    var selector = '#ProductSubmitButton-' + (window.CSS && CSS.escape ? CSS.escape(section) : section);
    var found = null;
    document.querySelectorAll(selector).forEach(function (btn) {
      if (btn.closest('sticky-atc')) return; // the bar's own button
      if (btn.offsetParent !== null || btn.getBoundingClientRect().height > 0) found = btn;
    });
    return found;
  }

  function repairStickyAtc() {
    document.querySelectorAll('sticky-atc[data-after-scroll]').forEach(function (bar) {
      var section = bar.dataset.section;
      if (!section) return;

      var button = visibleSubmitButton(section);
      if (!button || bar.mainAtcBtn === button) return;

      bar.mainAtcBtn = button;
      if (typeof bar.checkScroll === 'function') bar.checkScroll();
    });
  }

  /* ---------- 2. personalized bar ---------- */

  var PERSONALISATION_FIELDS = [
    'input[type="text"][name^="properties["]',
    'input[type="file"][name^="properties["]',
    'input[type="radio"][name^="properties["]',
    'textarea[name^="properties["]',
    'select[name^="properties["]',
  ].join(',');

  function hasPersonalisation(form) {
    var fields = form.querySelectorAll(PERSONALISATION_FIELDS);
    for (var i = 0; i < fields.length; i++) {
      // Bold hides its bookkeeping inputs; only a field the shopper can fill counts
      if (fields[i].offsetParent !== null) return true;
    }
    return false;
  }

  function togglePersonalizedBar() {
    document.querySelectorAll('.eb-personalized-bar').forEach(function (bar) {
      var scope = bar.parentElement;
      var form = scope && scope.querySelector('product-form');
      bar.classList.toggle('is-active', !!form && hasPersonalisation(form));
    });
  }


  /* ---------- 3. review tabs ---------- */

  /*
   * The product page carries two Judge.me widgets: this product's reviews and
   * the store-wide ones. Stacked, a product with reviews shows both lists back
   * to back, which reads as one endless wall of reviews. Put them behind two
   * tabs instead and open the one that is actually about this product whenever
   * it has something to show.
   */

  var TAB_LABELS = { product: 'Item Reviews', store: 'Store Reviews' };

  function outermost(nodes) {
    return nodes.filter(function (n) {
      return !nodes.some(function (other) { return other !== n && other.contains(n); });
    })[0];
  }

  function buildReviewTabs() {
    var product = document.querySelector('.jdgm-widget.jdgm-review-widget');
    var stores = [].slice.call(document.querySelectorAll('.jdgm-widget')).filter(function (w) {
      return /all-reviews/.test(w.className) && (!product || (!product.contains(w) && !w.contains(product)));
    });
    var store = outermost(stores);

    if (!product || !store) return false;                       // nothing to switch between
    if (document.querySelector('.eb-review-tabs')) return true;  // already built

    // each widget sits in its own .shopify-app-block; those are the siblings
    // that have to be toggled, not the Judge.me roots inside them
    product = product.closest('.shopify-app-block') || product;
    store = store.closest('.shopify-app-block') || store;
    if (product === store) return false;

    var parent = product.parentElement;
    if (!parent || !parent.contains(store)) return false;        // unexpected markup, leave it alone

    var nav = document.createElement('div');
    nav.className = 'eb-review-tabs';
    nav.setAttribute('role', 'tablist');

    var panes = { product: product, store: store };
    var buttons = {};

    Object.keys(panes).forEach(function (key) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'eb-review-tabs__tab';
      btn.setAttribute('role', 'tab');
      btn.textContent = TAB_LABELS[key];
      btn.addEventListener('click', function () { select(key); });
      nav.appendChild(btn);
      buttons[key] = btn;
    });

    function select(key) {
      Object.keys(panes).forEach(function (k) {
        panes[k].hidden = k !== key;
        buttons[k].classList.toggle('is-active', k === key);
        buttons[k].setAttribute('aria-selected', k === key ? 'true' : 'false');
      });
    }

    parent.insertBefore(nav, product.compareDocumentPosition(store) & Node.DOCUMENT_POSITION_FOLLOWING ? product : store);

    // open on this product's own reviews when it has any, otherwise the store's
    select(product.querySelectorAll('.jdgm-rev').length ? 'product' : 'store');
    return true;
  }

  /* Judge.me renders both widgets asynchronously, so keep trying briefly */
  function watchForReviewWidgets() {
    if (buildReviewTabs()) return;
    var tries = 0;
    var timer = setInterval(function () {
      if (buildReviewTabs() || ++tries > 40) clearInterval(timer);
    }, 400);
  }

  /* ---------- wiring ---------- */

  function run() {
    repairStickyAtc();
    togglePersonalizedBar();
  }

  document.addEventListener('DOMContentLoaded', run);
  document.addEventListener('DOMContentLoaded', watchForReviewWidgets);
  window.addEventListener('load', run);
  window.addEventListener('load', watchForReviewWidgets);
  // the quantity-selector block and Bold both re-render after load
  document.addEventListener('shopify:section:load', run);
  [400, 1200, 2500].forEach(function (delay) {
    window.setTimeout(run, delay);
  });
})();
